import { startOfToday, startOfTomorrow, subDays } from "date-fns"
import { LoaderFunctionArgs, redirect, RouteObject } from "react-router-dom"

import { OverviewData } from "../DashboardStorefrontInsights/StorefrontInsightsOverviewPanel"
import { RecipientReactionsData } from "../DashboardStorefrontInsights/StorefrontInsightsRecipientReactionsPanel"
import { ShippingDestinationsData } from "../DashboardStorefrontInsights/StorefrontInsightsShippingDestinationsPanel"
import { ThankYouNotesData } from "../DashboardStorefrontInsights/StorefrontInsightsThankYouNotesPanel"
import { TopUsersData } from "../DashboardStorefrontInsights/StorefrontInsightsTopUsersPanel"
import { DashboardStorefrontInsightsGenerateReportModal } from "~/src/components/DashboardStorefrontInsights/DashboardStorefrontInsightsGenerateReportModal"
import { DashboardStorefrontInsightsShow } from "~/src/components/DashboardStorefrontInsights/DashboardStorefrontInsightsShow"
import { OrderVolumeData } from "~/src/components/DashboardStorefrontInsights/StorefrontInsightsOrderVolumePanel"
import { PopularProductsData } from "~/src/components/DashboardStorefrontInsights/StorefrontInsightsPopularProductsPanel"
import { ErrorPage } from "~/src/components/ErrorPage"
import { appClient } from "~/src/lib/appClients"

export const basename = "/dashboard/storefront_insights"

type EndpointMap = {
  overview: OverviewData
  order_volume: OrderVolumeData
  popular_products: PopularProductsData
  shipping_destinations: ShippingDestinationsData
  top_users: TopUsersData
  thank_you_notes: ThankYouNotesData
  recipient_reactions: RecipientReactionsData
}

function buildInsightsLoader<K extends keyof EndpointMap>(endpoint: K) {
  return {
    path: endpoint as string,
    shouldRevalidate: () => false,
    loader: async ({ request }: LoaderFunctionArgs): Promise<EndpointMap[K]> => {
      const url = new URL(request.url)
      const { data } = await appClient.get(`${basename}/${endpoint}.json`, {
        headers: { Accept: "application/json" },
        params: url.searchParams,
      })

      return data as EndpointMap[K]
    },
  }
}
const createRoutes = (): RouteObject[] => [
  {
    id: "dashboardStorefrontInsightsShowRoute",
    path: basename,
    element: <DashboardStorefrontInsightsShow />,
    hydrateFallbackElement: <></>,
    errorElement: <ErrorPage />,
    shouldRevalidate: () => false,
    loader: async ({ request }: LoaderFunctionArgs) => {
      const url = new URL(request.url)

      const params = url.searchParams
      const areDatesMissing = ["all_time", "start_date", "end_date"].every((key) => !params.get(key))

      if (areDatesMissing) {
        params.set("start_date", subDays(startOfToday(), 29).toISOString()) // 29 days ago at midnight + today
        params.set("end_date", startOfTomorrow().toISOString())
        params.delete("all_time")
        return redirect(`${url.pathname}?${params.toString()}`)
      } else {
        const response = await appClient.get(`${basename}`, {
          headers: { Accept: "application/json" },
          params: url.searchParams,
        })
        return response.data
      }
    },
    children: [
      {
        path: "generate_report",
        element: <DashboardStorefrontInsightsGenerateReportModal />,
        action: async ({ request }) => {
          const formData = await request.formData()

          try {
            const response = await appClient.post(`${basename}/generate_report`, formData, {
              headers: {
                "Content-Type": "multipart/form-data",
                Accept: "application/json",
              },
            })

            return response.data
          } catch (error: unknown) {
            const axiosError = error as {
              response?: {
                data?: {
                  flash?: Array<{ error?: string }>
                  error?: string
                }
              }
            }
            const errorMessage =
              axiosError.response?.data?.flash?.[0]?.error ||
              axiosError.response?.data?.error ||
              "Failed to generate report. Please try again."
            return { flash: [{ error: errorMessage }] }
          }
        },
      },
      buildInsightsLoader("overview"),
      buildInsightsLoader("order_volume"),
      buildInsightsLoader("popular_products"),
      buildInsightsLoader("shipping_destinations"),
      buildInsightsLoader("top_users"),
      buildInsightsLoader("thank_you_notes"),
      buildInsightsLoader("recipient_reactions"),
    ],
  },
]

export default createRoutes
